import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useTimer } from '../../context/TimerContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useTheme } from '../../context/ThemeContext';
import { timeClockAPI, API_BASE_URL } from '../../services/api';
import { ClockIcon } from '../icons';

interface TimerCompletionModalProps {
  onClose: () => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Format duration to human-readable string
const formatDuration = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (hrs === 0) {
    return `${mins}m`;
  }
  return `${hrs}h ${mins}m`;
};

const TimerCompletionModal: React.FC<TimerCompletionModalProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const {
    activeTimer,
    elapsedSeconds,
    discardTimer,
    onTimerCompleted,
    isLoading,
  } = useTimer();
  const { confirm } = useConfirm();

  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [extractedDescription, setExtractedDescription] = useState<
    string | null
  >(null);
  const [showAiChat, setShowAiChat] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, streamingMessage]);

  // Auto-start conversation when modal opens
  useEffect(() => {
    if (activeTimer && chatMessages.length === 0) {
      const duration = formatDuration(elapsedSeconds);
      const projectName = activeTimer.project?.name || 'your project';

      // Send initial AI message
      const initialMessage = t('timer.aiInitialMessage', {
        projectName,
        duration,
      });

      // Start the conversation by asking about the work
      handleSendChatMessage(
        t('timer.whatDidYouWorkOnPrompt', {
          projectName,
          duration,
        }),
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract description from AI response
  const extractDescriptionFromMessage = (content: string): string | null => {
    const tagMatch = content.match(
      /\[DESCRIPTION\]\s*([\s\S]+?)\s*\[\/DESCRIPTION\]/i,
    );
    if (tagMatch) {
      return tagMatch[1].trim();
    }
    return null;
  };

  // Handle AI chat with streaming
  const handleSendChatMessage = async (message?: string) => {
    const messageToSend = message || chatInput.trim();
    if (!messageToSend) return;

    // Add user message to chat (unless it's the auto-start prompt)
    if (!message) {
      setChatMessages((prev) => [
        ...prev,
        { role: 'user', content: messageToSend },
      ]);
    }
    setChatInput('');
    setChatLoading(true);
    setStreamingMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/compliance/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          message: messageToSend,
          description: description,
          project_id: activeTimer?.project?.id,
          conversation_id: conversationId,
          stream: true,
          context: 'timer_completion',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullMessage = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.done) {
                  setConversationId(data.conversation_id);
                  setChatMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: fullMessage },
                  ]);
                  setStreamingMessage('');

                  // Check for extracted description in the response
                  const extracted = extractDescriptionFromMessage(fullMessage);
                  if (extracted) {
                    setExtractedDescription(extracted);
                  }
                } else if (data.error) {
                  console.error('Chat error:', data.error);
                } else if (typeof data === 'string') {
                  fullMessage += data;
                  setStreamingMessage(fullMessage);
                }
              } catch {
                // Skip malformed chunks
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to send chat message:', error);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t('ai.assistant.errorMessage') },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // Apply extracted description
  const handleApplyDescription = (desc: string) => {
    setDescription(desc);
    setExtractedDescription(null);
  };

  // Save time entry
  const handleSave = async () => {
    if (!activeTimer || !description.trim()) {
      setError(t('timer.descriptionRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await timeClockAPI.convertToTimeEntry(activeTimer.id, {
        description: description.trim(),
      });
      onTimerCompleted();
    } catch (err: any) {
      setError(err.response?.data?.error || t('timer.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  // Discard timer
  const handleDiscard = async () => {
    const confirmed = await confirm({
      title: t('timer.discardTitle'),
      message: t('timer.confirmDiscard'),
      confirmLabel: t('timer.discard'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await discardTimer();
    } catch (err: any) {
      setError(err.response?.data?.error || t('timer.failedToDiscard'));
    }
  };

  if (!activeTimer) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      style={{ zIndex: 9999 }}
    >
      <div
        className={`${isDark ? 'bg-slate-900' : 'bg-white'} rounded-xl shadow-2xl w-full max-w-lg my-auto flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-600 to-purple-600">
          <h2 className="text-lg font-semibold text-white">
            {t('timer.completeYourEntry')}
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Timer Summary - Compact */}
        <div
          className={`px-4 py-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'} border-b`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div
                className={`font-medium truncate text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {activeTimer.project?.name}
              </div>
              {activeTimer.project?.client?.name && (
                <div
                  className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {activeTimer.project.client.name}
                </div>
              )}
            </div>
            <div
              className={`text-xs whitespace-nowrap ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {format(new Date(activeTimer.started_at), 'MMM d')}
            </div>
          </div>
          <div className="flex items-end justify-between mt-2">
            <div className="font-bold text-2xl text-blue-600 leading-none">
              {formatDuration(elapsedSeconds)}
            </div>
            <div
              className={`flex items-center gap-1 text-sm font-medium tabular-nums ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
            >
              <ClockIcon className="w-4 h-4 shrink-0" />
              <span>
                {format(new Date(activeTimer.started_at), 'HH:mm')}
                {' – '}
                {format(
                  new Date(
                    new Date(activeTimer.started_at).getTime() +
                      elapsedSeconds * 1000,
                  ),
                  'HH:mm',
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 space-y-3">
          {/* Description Section */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('timer.whatDidYouWorkOn')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm ${isDark ? 'bg-slate-800 border border-slate-700 text-white placeholder-slate-400' : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-500'}`}
              placeholder={t('timer.descriptionPlaceholder')}
            />

            {/* Error Message */}
            {error && (
              <div className="mt-1 text-xs text-red-600 bg-red-50 px-2 py-1.5 rounded">
                {error}
              </div>
            )}

            {/* Extracted Description Banner */}
            {extractedDescription && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-900 bg-white p-2 rounded border border-green-200 mb-2">
                  {extractedDescription}
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleApplyDescription(extractedDescription)}
                    className="flex-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors font-medium"
                  >
                    {t('timer.applyDescription')}
                  </button>
                  <button
                    onClick={() => setExtractedDescription(null)}
                    className="px-2 py-1 border border-slate-300 text-slate-600 text-xs rounded hover:bg-slate-50 transition-colors"
                  >
                    {t('common.dismiss')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* AI Chat Section - Collapsible */}
          <div
            className={`${isDark ? 'border-slate-700' : 'border-slate-200'} border rounded-lg overflow-hidden`}
          >
            {/* Chat Header - Click to expand/collapse */}
            <button
              onClick={() => setShowAiChat(!showAiChat)}
              className={`w-full flex items-center justify-between px-3 py-2 transition-colors ${isDark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'}`}
            >
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 text-purple-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                <span
                  className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  {t('timer.aiHelp')}
                </span>
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${isDark ? 'text-slate-400' : 'text-slate-500'} ${showAiChat ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {showAiChat && (
              <>
                {/* Chat Messages */}
                <div
                  className={`overflow-y-auto p-2 space-y-2 max-h-[120px] ${isDark ? 'bg-slate-800' : 'bg-white'}`}
                >
                  {chatMessages.length === 0 && !streamingMessage && (
                    <div
                      className={`text-center py-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                    >
                      <p className="text-xs">{t('timer.aiChatHint')}</p>
                    </div>
                  )}

                  {chatMessages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-lg px-3 py-1.5 text-xs ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}

                  {streamingMessage && (
                    <div className="flex justify-start">
                      <div className="max-w-[85%] rounded-lg px-3 py-1.5 bg-slate-100 text-slate-900 text-xs">
                        <p className="whitespace-pre-wrap">
                          {streamingMessage}
                        </p>
                        <span className="inline-block w-1 h-3 bg-blue-600 animate-pulse ml-0.5"></span>
                      </div>
                    </div>
                  )}

                  {chatLoading && !streamingMessage && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 rounded-lg px-3 py-1.5">
                        <div className="flex space-x-1">
                          <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></div>
                          <div
                            className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"
                            style={{ animationDelay: '0.1s' }}
                          ></div>
                          <div
                            className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"
                            style={{ animationDelay: '0.2s' }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>

                {/* Chat Input */}
                <div
                  className={`px-2 py-1.5 border-t ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                >
                  <div className="flex space-x-1.5">
                    <input
                      ref={inputRef}
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === 'Enter' &&
                        !chatLoading &&
                        handleSendChatMessage()
                      }
                      placeholder={t('timer.chatPlaceholder')}
                      className={`flex-1 px-2 py-1 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-slate-800 border border-slate-700 text-white placeholder-slate-400' : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-500'}`}
                      disabled={chatLoading}
                    />
                    <button
                      onClick={() => handleSendChatMessage()}
                      disabled={chatLoading || !chatInput.trim()}
                      className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
        >
          <button
            onClick={handleDiscard}
            disabled={isLoading || saving}
            className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('timer.discardTimer')}
          </button>

          <button
            onClick={handleSave}
            disabled={saving || isLoading || !description.trim()}
            className="px-4 py-1.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.saving')}
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t('timer.saveEntry')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TimerCompletionModal;
